import React, { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';

import { PortfolioPlan } from '../../../models/portfolio/portfolio';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';

import { portfolioService } from '../../../services/portfolio.service';
import { eventBus } from '../../../services/event-emitter.service';

import DialogConfirm from '../../controls/dialogs/DialogConfirm';
import { authService } from '../../../services/auth.service';


interface PlannenActionMenuProps {
  params: GridRenderCellParams<PortfolioPlan>;
  activeParticipant?: boolean;
  mijnOrganisatieId?: string;
  canCrud?: boolean;
}

const RELOAD_PORTFOLIO_DELAY_MS = 5000;

const PlannenActionMenu = ({ 
  params,
  activeParticipant,
  mijnOrganisatieId,
  canCrud
}: PlannenActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [currentRow, setCurrentRow] = useState<PortfolioPlan | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    beoordeling: false,
    sharing: false,
    snackbar: false,
    confirm: false,
  });

  const [pendingAction, setPendingAction] = useState<{
    type: 'deelname' | 'afstemming';
    status: string;
    message: string;
    confirmTitle: string;
    confirmContent: string;
    confirmButtonText: string;
    nextAfstemmingStatus?: 'in_afstemming' | 'afgestemd';
  } | null>(null);

  useEffect(() => {
    setCurrentRow(params.row);
  }, [params]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setAnchorEl(event.currentTarget);
  };

  const navigateToPlan = (planId: string) => {
    window.open(`/plannen/${planId}`, '_blank', 'noopener,noreferrer');
    // navigate(`/plannen/${planId}`);
    handleCloseMenu();
  };

  const zoomInOnPlanBbox = () => {
    if (!currentRow) return;
    eventBus.emit('selectPlan', currentRow.infplId ?? null);
    eventBus.emit('zoomInOnPlanBbox', currentRow.bbox);
    handleCloseMenu();
  };

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    // setSelectedId(null);
  };

  const onClickUpdatePlanStatus = (status: string) => {
    if (!currentRow) return;
  
    let message = '';
    let confirmTitle = '';
    let confirmContent = '';
    let confirmButtonText = '';
  
    // Validatie en tekst op basis van status
    switch (status) {
      case 'accepted':
        if (currentRow.inclusionRequestStatus !== 'pending') {
          setSnackbarMessage('Deelname status kan alleen worden aangepast voor openstaande deelnameverzoeken');
          openDialog('snackbar');
          return;
        }
        message = 'Deelname aan het afstemmingsdossier is geaccepteerd';
        confirmTitle = 'Deelname accepteren';
        confirmContent = 'Weet je zeker dat je deelname aan dit dossier wilt accepteren?';
        confirmButtonText = 'Accepteren';
        break;
  
      case 'declined':
        if (currentRow.inclusionRequestStatus !== 'pending') {
          setSnackbarMessage('Deelname status kan alleen worden aangepast voor openstaande deelnameverzoeken');
          openDialog('snackbar');
          return;
        }
        message = 'Deelname aan het afstemmingsdossier is geweigerd';
        confirmTitle = 'Deelname weigeren';
        confirmContent = 'Weet je zeker dat je deelname aan dit dossier wilt weigeren?';
        confirmButtonText = 'Weigeren';
        break;
  
      case 'left':
        if (currentRow.inclusionRequestStatus !== 'accepted') {
          setSnackbarMessage('Deelname status kan alleen worden gestopt voor geaccepteerde deelnameverzoeken');
          openDialog('snackbar');
          return;
        }
        message = 'Deelname aan het afstemmingsdossier is gestopt';
        confirmTitle = 'Stop deelname';
        confirmContent = 'Weet je zeker dat je wilt stoppen met deelname aan dit dossier?';
        confirmButtonText = 'Stoppen';
        break;
  
      case 'cancelled':
        if (currentRow.inclusionRequestStatus !== 'pending') {
          setSnackbarMessage('Alleen openstaande deelnameverzoeken kunnen worden geannuleerd');
          openDialog('snackbar');
          return;
        }
        message = 'De uitnodiging tot deelname aan afstemmingsdossier is geannuleerd';
        confirmTitle = 'Uitnodiging annuleren';
        confirmContent = 'Weet je zeker dat je de uitnodiging tot deelname wilt annuleren?';
        confirmButtonText = 'Annuleren';
        break;
  
      default:
        return;
    }
  
    // Zet pending actie en open bevestigingsdialoog
    setPendingAction({
      type: 'deelname',
      status,
      message,
      confirmTitle,
      confirmContent,
      confirmButtonText,
    });
  
    openDialog('confirm');
    handleCloseMenu();
  };
  
  const handleConfirm = () => {
    if (pendingAction) {
      if (pendingAction.type === 'afstemming') {
        updatePlanAfstemmingStatus(pendingAction.nextAfstemmingStatus || 'afgestemd', pendingAction.message);
      }
      else {
        updatePlanStatus(pendingAction.status, pendingAction.message);
      }
    }
    
    closeDialog('confirm');
    setTimeout(() => {
      setPendingAction(null);
    }, 100);
  };

  const updatePlanStatus = async (status: string, message: string = 'Deelname status van het plan is aangepast') => {
    try {
      setLoading(true);

      const deelnameId = currentRow ? currentRow.inclusionRequestId : null;
      if (!deelnameId) {
        setError('Geen deelname verzoek gevonden voor dit plan.');
        return;
      }

      let response;
      if (status !== 'cancelled') {
        response = await portfolioService.updatePlanDeelnameStatus(status, deelnameId);
        if (!response) {
          setLoading(false);
          // Handle error based on HTTP status code
          const errorMessage = await response.text();
          console.error('Failed to update portfolio:', errorMessage);
          throw new Error(errorMessage);
        }
      } 
      else if (status === 'cancelled') {
        response = await portfolioService.deletePlanDeelnameVerzoek(deelnameId);
        if (!response.ok) {
          setLoading(false);
          throw new Error('Verzoek tot verwijderen van deelname is mislukt');
        }
      }
      
      setSnackbarMessage(message);
      openDialog('snackbar');
      setTimeout(() => {
        setLoading(false);
        eventBus.emit('reloadPortfolio', status);
      }, RELOAD_PORTFOLIO_DELAY_MS);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating status deelname:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const canToggleAfstemming = !!currentRow &&
    !!canCrud &&
    currentRow.organizationId === mijnOrganisatieId &&
    (currentRow.inclusionRequestStatus ?? '').toLowerCase() === 'accepted' &&
    ((currentRow.planAfstemmingsStatus ?? '').toLowerCase() === 'in_afstemming' ||
      (currentRow.planAfstemmingsStatus ?? '').toLowerCase() === 'afgestemd');

  const onClickToggleAfstemmingStatus = () => {
    if (!currentRow || !canToggleAfstemming) {
      return;
    }

    const currentStatus = (currentRow.planAfstemmingsStatus ?? '').toLowerCase();
    const nextStatus = currentStatus === 'afgestemd' ? 'in_afstemming' : 'afgestemd';

    setPendingAction({
      type: 'afstemming',
      status: nextStatus,
      nextAfstemmingStatus: nextStatus as 'in_afstemming' | 'afgestemd',
      message: nextStatus === 'afgestemd'
        ? 'Plan is op afgestemd gezet'
        : 'Plan is teruggezet naar in afstemming',
      confirmTitle: nextStatus === 'afgestemd'
        ? 'Plan op afgestemd zetten'
        : 'Plan terug naar in afstemming zetten',
      confirmContent: nextStatus === 'afgestemd'
        ? `Weet je zeker dat je plan ${currentRow.planNaam} op afgestemd wilt zetten?`
        : `Weet je zeker dat je plan ${currentRow.planNaam} terug naar in afstemming wilt zetten?`,
      confirmButtonText: 'Ja dat weet ik zeker',
    });

    openDialog('confirm');
    handleCloseMenu();
  };

  const updatePlanAfstemmingStatus = async (
    nextStatus: 'in_afstemming' | 'afgestemd',
    message: string
  ) => {
    try {
      setLoading(true);

      const deelnameId = currentRow ? currentRow.inclusionRequestId : null;
      if (!deelnameId) {
        setError('Geen deelname verzoek gevonden voor dit plan.');
        return;
      }

      await portfolioService.updatePlanAfstemmingStatus(nextStatus, deelnameId);

      if (nextStatus === 'in_afstemming') {
        const planNaam = currentRow?.planNaam || 'onbekend plan';
        eventBus.emit('openAnnotationPrefill', {
          title: `${planNaam} terug naar in afstemming`,
          description: `Notitie: Ik heb de afstemmingsstatus van plan ${planNaam} weer naar ‘in afstemming’ gezet omdat...`,
        });
        eventBus.emit('openDialog', 'annotation');
      }

      setSnackbarMessage(message);
      openDialog('snackbar');

      setTimeout(() => {
        setLoading(false);
        eventBus.emit('reloadPortfolio', nextStatus);
      }, 1000);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating afstemmingstatus:', error);
      setError('Er is een fout opgetreden');
    }
  };

  if (loading) {
    return <ProgressBar />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key="topcenter"
      />

      <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
        <Tooltip title="Acties" placement="right" arrow>
          <Icon>more_vert</Icon>
        </Tooltip>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => navigateToPlan(selectedId || '')}>
          <ListItemIcon>
            <Icon>map</Icon>
          </ListItemIcon>
          Open plan
        </MenuItem>
        <MenuItem onClick={() => zoomInOnPlanBbox()}>
          <ListItemIcon>
            <Icon>location_on</Icon>
          </ListItemIcon>
          Toon plan op kaart
        </MenuItem>

        {/* mijnOrganisatieId */}
        { currentRow && currentRow.inclusionRequestStatus === 'pending' && authService.canCrud() && (
          <>
            {/* Accepteren en weigeren mag alleen de eigenaar van het plan */}
            { currentRow.organizationId === mijnOrganisatieId && (
              <>
                <MenuItem onClick={() => onClickUpdatePlanStatus('accepted')}>
                  <ListItemIcon>
                    <Icon>check</Icon>
                  </ListItemIcon>
                  Accepteer deelname
                </MenuItem>

                <MenuItem onClick={() => onClickUpdatePlanStatus('declined')}>
                  <ListItemIcon>
                    <Icon>close</Icon>
                  </ListItemIcon>
                  Weiger deelname
                </MenuItem>
              </>
            )}

            {/* Annuleren mag alleen de activeParticipant van het afst. dossier */}
            { activeParticipant && (
              <>
                <MenuItem onClick={() => onClickUpdatePlanStatus('cancelled')}>
                  <ListItemIcon>
                    <Icon>cancel_schedule_send</Icon>
                  </ListItemIcon>
                  Annuleer de uitnodiging
                </MenuItem>
              </>
            )}
          </>
        )}

        { currentRow && 
          currentRow.inclusionRequestStatus === 'accepted' && 
          currentRow.organizationId === mijnOrganisatieId && 
          authService.canCrud() && (
          <>
            <MenuItem onClick={() => onClickUpdatePlanStatus('left')}>
              <ListItemIcon>
                <Icon>block</Icon>
              </ListItemIcon>
              Stop deelname
            </MenuItem>
          </>
        )}

        {canToggleAfstemming && (
          <MenuItem onClick={onClickToggleAfstemmingStatus}>
            <ListItemIcon>
              <Icon color={(currentRow?.planAfstemmingsStatus ?? '').toLowerCase() === 'afgestemd' ? 'warning' : 'success'}>
                {(currentRow?.planAfstemmingsStatus ?? '').toLowerCase() === 'afgestemd' ? 'toggle_off' : 'toggle_on'}
              </Icon>
            </ListItemIcon>
            {(currentRow?.planAfstemmingsStatus ?? '').toLowerCase() === 'afgestemd'
              ? 'Zet terug naar in afstemming'
              : 'Zet op afgestemd'}
          </MenuItem>
        )}
      </Menu>
      
      <DialogConfirm
        open={open.confirm}
        title={pendingAction?.confirmTitle || 'Bevestigen'}
        contentText={pendingAction?.confirmContent || 'Weet je zeker dat je deze actie wilt uitvoeren?'}
        confirmButtonText={pendingAction?.confirmButtonText || 'Ja dat weet ik zeker'}
        onClose={() => {
          closeDialog('confirm');
          setTimeout(() => {
            setPendingAction(null);
          }, 100);
        }}
        onConfirm={handleConfirm}
      />

    </>
  );
};

export default PlannenActionMenu;
